const DEFAULT_BASE_URL = 'https://luckynote.io';
const DIRECT_IMAGE_URL_PATTERN = /\.(jpeg|jpg|gif|png)$/i;

const getUrlPathname = source => {
    if (!source || typeof source !== 'string') {
        return null;
    }

    try {
        return new URL(source, typeof window !== 'undefined' ? window.location.origin : DEFAULT_BASE_URL).pathname;
    } catch (error) {
        return null;
    }
};

const normalizeStorageKey = key => {
    if (typeof key !== 'string') {
        return null;
    }

    const normalized = key.trim().replace(/^\/+/, '');
    return normalized || null;
};

export const isBunnyCdnUrl = source => {
    if (!source || typeof source !== 'string') {
        return false;
    }

    try {
        const parsed = new URL(source, typeof window !== 'undefined' ? window.location.origin : DEFAULT_BASE_URL);
        return parsed.hostname.toLowerCase().endsWith('.b-cdn.net');
    } catch (error) {
        return false;
    }
};

export const isUrlMetaCdnUrlForStoredKey = (cdnUrl, storedKey) => {
    const key = normalizeStorageKey(storedKey);
    if (!key) {
        return false;
    }

    return getUrlPathname(cdnUrl) === `/${key}`;
};

const getUsableUrlMetaCdnUrl = (targetMeta, isAuthor = false) => {
    const cdnUrl = isAuthor ? targetMeta?.author_image_cdn_url : targetMeta?.image_cdn_url;
    const storageKey = isAuthor
        ? (targetMeta?.author_image_name || targetMeta?.author_image_public_id)
        : (targetMeta?.image_name || targetMeta?.public_id);

    if (isBunnyCdnUrl(cdnUrl) && !isUrlMetaCdnUrlForStoredKey(cdnUrl, storageKey)) {
        return null;
    }

    return cdnUrl;
};

export const isVideoLikeMediaType = mediaType => {
    const normalizedType = String(mediaType || '').trim().toLowerCase();

    if (!normalizedType) {
        return false;
    }

    return (
        normalizedType === 'video' ||
        normalizedType.startsWith('video/') ||
        normalizedType.includes('mp4') ||
        normalizedType.includes('webm') ||
        normalizedType.includes('mov') ||
        normalizedType.includes('m4v') ||
        normalizedType.includes('ogv') ||
        normalizedType.includes('mkv') ||
        normalizedType.includes('mpegurl') ||
        normalizedType.includes('m3u8')
    );
};

export const isExpiredSignedMediaUrl = source => {
    if (!source || typeof source !== 'string') {
        return false;
    }

    try {
        const parsed = new URL(source, typeof window !== 'undefined' ? window.location.origin : DEFAULT_BASE_URL);
        const expires = parsed.searchParams.get('expires');

        if (expires && /^\d+$/.test(expires)) {
            return Number(expires) * 1000 <= Date.now();
        }

        const amzDate = parsed.searchParams.get('X-Amz-Date');
        const amzExpires = parsed.searchParams.get('X-Amz-Expires');

        if (!amzDate || !amzExpires || !/^\d+$/.test(amzExpires)) {
            return false;
        }

        const match = amzDate.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
        if (!match) {
            return false;
        }

        const signedAt = Date.UTC(
            Number(match[1]),
            Number(match[2]) - 1,
            Number(match[3]),
            Number(match[4]),
            Number(match[5]),
            Number(match[6])
        );

        return signedAt + (Number(amzExpires) * 1000) <= Date.now();
    } catch (error) {
        return false;
    }
};

export const getUsableSignedMediaUrl = source => (
    isExpiredSignedMediaUrl(source) ? null : source
);

const isLuckynoteS3LinkPreviewUrl = source => {
    if (!source || typeof source !== 'string') {
        return false;
    }

    try {
        const parsed = new URL(source, typeof window !== 'undefined' ? window.location.origin : DEFAULT_BASE_URL);
        const host = parsed.hostname.toLowerCase();
        return (
            (host === 'luckynote.s3.amazonaws.com' || host.startsWith('luckynote.s3.')) &&
            parsed.pathname.includes('/link-previews/')
        );
    } catch (error) {
        return false;
    }
};

const getUsableUrlMetaPrimaryImage = source => {
    if (isLuckynoteS3LinkPreviewUrl(source)) {
        return null;
    }

    return getUsableSignedMediaUrl(source);
};

const getUsableUrlMetaImageSource = source => {
    if (isLuckynoteS3LinkPreviewUrl(source)) {
        return null;
    }

    return getUsableSignedMediaUrl(source);
};

const isTwitterVideoUrl = source => {
    if (!source || typeof source !== 'string') {
        return false;
    }

    try {
        const parsed = new URL(source, typeof window !== 'undefined' ? window.location.origin : DEFAULT_BASE_URL);
        return parsed.hostname.toLowerCase() === 'video.twimg.com';
    } catch (error) {
        return false;
    }
};

const getPlayableExternalMediaUrl = source => {
    const usableSource = getUsableSignedMediaUrl(source);
    if (!usableSource || isTwitterVideoUrl(usableSource)) {
        return null;
    }

    return usableSource;
};

export const isLuckynoteS3MediaUrl = source => {
    const usableSource = getUsableSignedMediaUrl(source);
    if (!usableSource) {
        return false;
    }

    try {
        const hostname = new URL(usableSource, typeof window !== 'undefined' ? window.location.origin : DEFAULT_BASE_URL)
            .hostname
            .toLowerCase();
        return hostname === 'luckynote.s3.amazonaws.com' || hostname.startsWith('luckynote.s3.');
    } catch (error) {
        return false;
    }
};

export const getUrlMetaDirectS3VideoSource = targetMeta => {
    const mediaUrl = getUsableSignedMediaUrl(targetMeta?.media_url);
    return isLuckynoteS3MediaUrl(mediaUrl) ? mediaUrl : null;
};

export const decodeProviderMediaUrl = source => (
    typeof source === 'string' ? source.replace(/&amp;/g, '&') : source
);

export const getUrlMetaVideoSource = targetMeta => {
    if (!targetMeta) {
        return null;
    }

    return (
        getUrlMetaDirectS3VideoSource(targetMeta) ||
        targetMeta.media_cdn_url ||
        getPlayableExternalMediaUrl(decodeProviderMediaUrl(targetMeta.media_hls_url)) ||
        getPlayableExternalMediaUrl(decodeProviderMediaUrl(targetMeta.media_url)) ||
        null
    );
};

export const isUrlMetaVideo = targetMeta =>
    Boolean(getUrlMetaVideoSource(targetMeta)) &&
    (
        isVideoLikeMediaType(targetMeta?.media_type) ||
        Boolean(targetMeta?.media_hls_url || targetMeta?.media_cdn_url) ||
        /\.(mp4|webm|mov|m4v|ogv|m3u8)(?:[?#]|$)/i.test(String(targetMeta?.media_url || ''))
    );

export const getUrlMetaOriginalVideoUrl = targetMeta => (
    decodeProviderMediaUrl(getUsableSignedMediaUrl(targetMeta?.media_url) || targetMeta?.media_hls_url || targetMeta?.media_cdn_url || null)
);

export const normalizeMediaUrl = source => {
    if (!source || typeof source !== 'string') {
        return null;
    }

    const baseUrl = typeof window !== 'undefined' ? window.location.origin : DEFAULT_BASE_URL;

    try {
        return new URL(source, baseUrl).toString();
    } catch (error) {
        return source;
    }
};

const getUniqueSources = sources => {
    const seen = new Set();

    return sources.filter(source => {
        if (!source || typeof source !== 'string' || source.trim().length === 0) {
            return false;
        }

        const usableSource = getUsableUrlMetaImageSource(source);
        if (!usableSource) {
            return false;
        }

        const normalized = normalizeMediaUrl(usableSource);
        if (!normalized || seen.has(normalized)) {
            return false;
        }

        seen.add(normalized);
        return true;
    });
};

export const getUniqueMediaSources = sources => getUniqueSources(Array.isArray(sources) ? sources : []);

export const isCloudinaryUrlMetaImage = (targetMeta, isAuthor = false) => {
    const publicId = isAuthor ? targetMeta?.author_image_public_id : targetMeta?.public_id;
    const imageUrl = isAuthor ? targetMeta?.author_image : targetMeta?.image;

    return !!(
        publicId &&
        typeof publicId === 'string' &&
        publicId.length > 0 &&
        !publicId.startsWith('http') &&
        (publicId.includes('cloudinary') || imageUrl?.includes('cloudinary.com'))
    );
};

export const getUrlMetaImageSources = (targetMeta, isAuthor = false) => {
    if (!targetMeta) {
        return [];
    }

    if (isCloudinaryUrlMetaImage(targetMeta, isAuthor)) {
        return getUniqueSources(
            isAuthor
                ? [targetMeta?.author_image, targetMeta?.author_image_original]
                : [targetMeta?.image_cdn_url, targetMeta?.image]
        );
    }

    const thumbnail = isAuthor ? targetMeta?.author_image_thumbnail : targetMeta?.thumbnail;
    const cdnUrl = getUsableUrlMetaCdnUrl(targetMeta, isAuthor);
    const primaryUrl = isAuthor
        ? getUsableSignedMediaUrl(targetMeta?.author_image)
        : getUsableUrlMetaPrimaryImage(targetMeta?.image);
    const gallerySources = !isAuthor && Array.isArray(targetMeta?.gallery_images)
        ? targetMeta.gallery_images
        : [];
    const originalUrl = isAuthor ? targetMeta?.author_image_original : null;
    const mediaUrl = !isAuthor && !isVideoLikeMediaType(targetMeta?.media_type) && !targetMeta?.media_hls_url
        ? targetMeta?.media_url
        : null;

    if (thumbnail) {
        return getUniqueSources([
            thumbnail.lg,
            thumbnail.xl,
            thumbnail.md,
            cdnUrl,
            thumbnail.original,
            primaryUrl,
            ...gallerySources,
            originalUrl,
            mediaUrl
        ]);
    }

    return getUniqueSources([cdnUrl, primaryUrl, ...gallerySources, originalUrl, mediaUrl]);
};

export const getUrlMetaDirectImageUrl = targetMeta => {
    const directUrl = targetMeta?.url;

    // The source link is only a fallback for metadata without a saved preview.
    // A direct image URL must not bypass hosted delivery and image recovery.
    if (typeof directUrl !== 'string' || getUrlMetaImageSources(targetMeta).length > 0) {
        return null;
    }

    return DIRECT_IMAGE_URL_PATTERN.test(directUrl) ? directUrl : null;
};

export const resolveUrlMetaPreviewMedia = targetMeta => {
    if (!targetMeta) {
        return {
            directImageUrl: null,
            mainSources: [],
            authorSources: [],
            sourceChain: [],
            primarySource: null,
            fallbackSource: null,
            isAuthorImage: false
        };
    }

    const directImageUrl = getUrlMetaDirectImageUrl(targetMeta);
    const mainSources = getUrlMetaImageSources(targetMeta, false);
    const authorSources = getUrlMetaImageSources(targetMeta, true);
    const sourceChain = directImageUrl
        ? getUniqueSources([directImageUrl])
        : (mainSources.length > 0 ? mainSources : authorSources);

    return {
        directImageUrl,
        mainSources,
        authorSources,
        sourceChain,
        primarySource: sourceChain[0] || null,
        fallbackSource: sourceChain[1] || null,
        isAuthorImage: !directImageUrl && mainSources.length === 0 && authorSources.length > 0
    };
};

export const getNextMediaSource = (failedSource, sourceChain) => {
    if (!Array.isArray(sourceChain) || sourceChain.length === 0) {
        return null;
    }

    const normalizedFailedSource = normalizeMediaUrl(failedSource);
    const currentIndex = sourceChain.findIndex(source => normalizeMediaUrl(source) === normalizedFailedSource);

    if (currentIndex === -1) {
        return sourceChain[0] || null;
    }

    return sourceChain[currentIndex + 1] || null;
};

export const shouldUseAnonymousCrossOrigin = source => {
    if (!source || typeof source !== 'string') {
        return false;
    }

    const baseUrl = typeof window !== 'undefined' ? window.location.origin : DEFAULT_BASE_URL;

    try {
        const { hostname } = new URL(source, baseUrl);
        const normalizedHost = hostname.toLowerCase();

        return (
            normalizedHost.endsWith('.b-cdn.net') ||
            normalizedHost.includes('amazonaws.com') ||
            normalizedHost.endsWith('.cloudinary.com') ||
            normalizedHost === 'res.cloudinary.com' ||
            normalizedHost.endsWith('luckynote.io')
        );
    } catch (error) {
        return false;
    }
};

export const getCrossOriginAttr = source => (
    shouldUseAnonymousCrossOrigin(source) ? 'anonymous' : undefined
);
